import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { getRequest, postRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import { toast } from "react-toastify";

const AddPayoutModal: React.FC<any> = ({ show, onHide }) => {
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [commissionLoading, setCommissionLoading] = useState(false);

    const [affiliateUserList, setAffiliateUserList] = useState<any[]>([]);
    const [commissionList, setCommissionList] = useState<any[]>([]);

    const [affiliateId, setAffiliateId] = useState("");
    const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
    const [amount, setAmount] = useState("0");
    const [remark, setRemark] = useState("");
    // Fetch Affiliate List
    const fetchAffiliatePayoutUserList = async () => {
        try {
            setLoading(true);

            const response = await getRequest(
                "/affiliate/affiliate-payout-user-list"
            );

            if (response.status === "success") {
                setAffiliateUserList(response.data);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch Commission List
    const fetchCommissionList = async (id: string) => {
        try {
            setCommissionLoading(true);
            setSelectedCommissions([]);
            setAmount("0");

            const response = await getRequest(
                `/affiliate/affiliate-payout-commission-list?affiliateId=${id}`
            );

            if (response.status === "success") {
                setCommissionList(response.data);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCommissionLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchAffiliatePayoutUserList();
        }
    }, [show]);

    // Change Affiliate
    const handleAffiliateChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const id = e.target.value;
        setAffiliateId(id);

        if (id) {
            fetchCommissionList(id);
        } else {
            setCommissionList([]);
            setSelectedCommissions([]);
            setAmount("0");
        }
    };

    // Select Commission Checkbox
    const handleCommissionSelect = (
        commissionId: string,
        commissionAmount: number
    ) => {
        let updatedSelections = [...selectedCommissions];

        if (updatedSelections.includes(commissionId)) {
            updatedSelections = updatedSelections.filter(
                (id) => id !== commissionId
            );
        } else {
            updatedSelections.push(commissionId);
        }

        setSelectedCommissions(updatedSelections);

        // Calculate Amount
        const total = commissionList
            .filter((item) =>
                updatedSelections.includes(item._id)
            )
            .reduce(
                (sum, item) =>
                    sum + Number(item.ac_amount || 0),
                0
            );

        setAmount(total.toString());
    };

    const handleSubmit = async () => {
        if (!affiliateId) {
            toast.error("Please select affiliate");
            return;
        }

        if (selectedCommissions.length === 0) {
            toast.error("Please select commissions");
            return;
        }

        try {
            setSubmitLoading(true);

            const response = await postRequest(
                "/affiliate/add-payout-proccess",
                {
                    affiliateId,
                    commissionIds: selectedCommissions,
                    amount,
                    remark,
                }
            );

            if (response.status === "success") {
                toast.success(response.message);

                setAffiliateId("");
                setCommissionList([]);
                setSelectedCommissions([]);
                setAmount("0");
                setRemark("");

                onHide();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Add Payout</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form>
                    <div className="row">
                        {/* Affiliate */}
                        <div className="col-lg-6 mb-3">
                            <Form.Group>
                                <Form.Label>Affiliate</Form.Label>

                                <Form.Select
                                    value={affiliateId}
                                    onChange={handleAffiliateChange}
                                >
                                    <option value="">
                                        Select Affiliate
                                    </option>

                                    {affiliateUserList.map((user) => (
                                        <option
                                            key={user._id}
                                            value={user._id}
                                        >
                                            {user.affiliate_first_name}{" "}
                                            {user.affiliate_last_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>

                        {/* Amount */}
                        <div className="col-lg-6 mb-3">
                            <Form.Group>
                                <Form.Label>Total Amount</Form.Label>

                                <Form.Control
                                    type="number"
                                    value={amount}
                                    readOnly
                                />
                            </Form.Group>
                        </div>

                        {/* Commission Select List */}
                        <div className="col-lg-12 mb-3">
                            <Form.Label>
                                Select Commissions
                            </Form.Label>

                            <div className="border rounded p-3">
                                {commissionLoading ? (
                                    <p className="mb-0">
                                        Loading...
                                    </p>
                                ) : commissionList.length > 0 ? (
                                    <div
                                        style={{
                                            maxHeight: "250px",
                                            overflowY: "auto",
                                        }}
                                    >
                                        {commissionList.map(
                                            (item: any) => (
                                                <div
                                                    key={item._id}
                                                    className="form-check mb-2"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={
                                                            item._id
                                                        }
                                                        checked={selectedCommissions.includes(
                                                            item._id
                                                        )}
                                                        onChange={() =>
                                                            handleCommissionSelect(
                                                                item._id,
                                                                item.ac_amount
                                                            )
                                                        }
                                                    />

                                                    <label
                                                        className="form-check-label"
                                                        htmlFor={
                                                            item._id
                                                        }
                                                    >
                                                        Order:{" "}
                                                        {
                                                            item.ac_order_id
                                                        }{" "}
                                                        | ₹
                                                        {
                                                            item.ac_amount
                                                        }
                                                    </label>
                                                </div>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    <p className="mb-0 text-muted">
                                        No commissions found
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Remark */}
                        <div className="col-lg-12 mb-3">
                            <Form.Group>
                                <Form.Label>Remark</Form.Label>

                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={remark}
                                    onChange={(e) =>
                                        setRemark(e.target.value)
                                    }
                                    placeholder="Enter remark"
                                />
                            </Form.Group>
                        </div>
                    </div>
                </Form>
            </Modal.Body> 
            <Modal.Footer>
                <Button
                    variant="outline-secondary"
                    onClick={onHide}
                >
                    Close
                </Button>

                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitLoading || selectedCommissions.length === 0}
                >
                    {submitLoading
                        ? "Processing..."
                        : "Submit"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddPayoutModal;